import { useCallback, useEffect, useRef } from 'react';

import type {
  AnalyticsWidgetStats,
  AnalyticsWidgetStatsRequest,
  GetAllStatsWidgetAnalyticsWidgetStatsPostApiArg,
} from '@shared/api';
import { useGetAllStatsWidgetAnalyticsWidgetStatsPostMutation } from '@shared/api';

type WidgetStatsResult = {
  data?: AnalyticsWidgetStats;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
};

export const useWidgetStats = (
  params: AnalyticsWidgetStatsRequest | null,
  enabled = true,
): WidgetStatsResult => {
  const [trigger, result] = useGetAllStatsWidgetAnalyticsWidgetStatsPostMutation();
  const lastArgsRef = useRef<GetAllStatsWidgetAnalyticsWidgetStatsPostApiArg | null>(null);

  const fetch = useCallback(
    async (request: AnalyticsWidgetStatsRequest) => {
      const args: GetAllStatsWidgetAnalyticsWidgetStatsPostApiArg = {
        analyticsWidgetStatsRequest: request,
      };

      lastArgsRef.current = args;

      try {
        await trigger(args).unwrap();
      } catch {}
    },
    [trigger],
  );

  const refetch = useCallback(() => {
    if (!lastArgsRef.current) return;

    void trigger(lastArgsRef.current)
      .unwrap()
      .catch(() => {});
  }, [trigger]);

  useEffect(() => {
    if (!enabled || !params) return;
    void fetch(params);
  }, [enabled, fetch, params]);

  return {
    data: result.data,
    isLoading: result.isLoading,
    isError: result.isError,
    refetch,
  };
};
